import { c as _c } from "react/compiler-runtime";
import { ensureScript } from '@/utils/ensureScript';
import type { MutableRefObject } from 'react';
import { useEffect } from 'react';

/**
 * A useEffect hook that embeds tweets into the page by looking for specific node attributes.
 * This helper relies on twitter's widget API and follows the recommended approach to avoid scanning all the DOM.
 * This implementation keeps the contents of a container intact unless twits can be retrieved.
 * https://developer.twitter.com/en/docs/twitter-for-websites/embedded-tweets/overview
 */
export const useTweetLoader = (ref, targetNodesSelector, targetAttribute, newNodeClass, targetStatusUrlAttribute) => {
  const $ = _c(8);
  let t0;
  if ($[0] !== newNodeClass || $[1] !== ref || $[2] !== targetAttribute || $[3] !== targetNodesSelector || $[4] !== targetStatusUrlAttribute) {
    t0 = () => {
      if (!ref.current) {
        return;
      }
      const elementTargets = ref.current.querySelectorAll(targetNodesSelector);
      if (elementTargets.length === 0) {
        return;
      }
      ensureScript("https://platform.twitter.com/widgets.js").then(() => {
        if (!ref.current || !window.hasOwnProperty("twttr")) {
          return;
        }
        const twttr = (window as any).twttr;
        for (const element of elementTargets) {
          if (!element.isConnected) {
            continue;
          }
          const twitId = element.getAttribute(targetAttribute) || getTweetIdFromStatusUrl(element.getAttribute(targetStatusUrlAttribute));
          if (twitId) {
            const newNode = document.createElement("div");
            newNode.className = newNodeClass;
            element.parentNode.insertBefore(newNode, element);
            twttr.widgets.createTweet(twitId, newNode, {
              dnt: true
            }).then(() => {
              if (newNode.innerHTML.length === 0) {
                console.info(`[Twitter] Attempted to create Twitter Widget for tweet ${twitId} but no content was added. This usually happens when the tweet was deleted`);
                newNode.parentNode?.removeChild(newNode);
              } else {
                element.parentNode?.removeChild(element);
              }
            }).catch(_temp);
          }
        }
      }).catch(_temp2);
    };
    $[0] = newNodeClass;
    $[1] = ref;
    $[2] = targetAttribute;
    $[3] = targetNodesSelector;
    $[4] = targetStatusUrlAttribute;
    $[5] = t0;
  } else {
    t0 = $[5];
  }
  let t1;
  if ($[6] !== ref) {
    t1 = [ref];
    $[6] = ref;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  useEffect(t0, t1);
  return ref;
};
export const getTweetIdFromStatusUrl = (targetUrl: string) => {
  const matches = /.+\/([0-9]+)$/g.exec(targetUrl);
  if (matches.length > 0) {
    return matches[1];
  }
  return null;
};
function _temp(e) {
  console.warn(e.message);
}
function _temp2(e_0) {
  console.warn(e_0.message);
}